/**
 * Finance Service
 * Comprehensive financial and accounting service for church management
 * Implements IAS-compliant financial operations
 */

import { supabase } from '@/lib/supabase'

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface ChartOfAccount {
  id: string
  church_id: string
  account_code: string
  account_name: string
  account_type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense'
  account_subtype: string
  parent_account_id?: string
  is_active: boolean
  is_system_account: boolean
  allow_manual_entry: boolean
  balance_sheet_category?: string
  income_statement_category?: string
  cash_flow_category?: string
  opening_balance: number
  opening_balance_date?: string
  current_balance: number
  last_transaction_date?: string
  restriction_type?: string
  description?: string
  notes?: string
  created_at: string
  updated_at: string
}

export interface JournalEntry {
  id: string
  church_id: string
  entry_number: string
  entry_date: string
  posting_date?: string
  source_type: string
  source_reference_id?: string
  status: 'draft' | 'posted' | 'voided' | 'approved'
  fiscal_year: number
  fiscal_period: number
  description: string
  reference?: string
  total_debit: number
  total_credit: number
  requires_approval: boolean
  approved_by?: string
  approved_at?: string
  is_reversal: boolean
  reverses_entry_id?: string
  reversed_by_entry_id?: string
  lines?: JournalEntryLine[]
  created_at: string
  updated_at: string
}

export interface JournalEntryLine {
  id: string
  journal_entry_id: string
  church_id: string
  line_number: number
  account_id: string
  debit_amount: number
  credit_amount: number
  description?: string
  department_id?: string
  project_id?: string
  fund_id?: string
  account?: ChartOfAccount
}

export interface Contribution {
  id: string
  church_id: string
  member_id?: string
  contributor_name?: string
  contributor_email?: string
  contributor_phone?: string
  is_anonymous: boolean
  contribution_date: string
  contribution_type: string
  contribution_method: string
  amount: number
  currency: string
  fund_id?: string
  designation?: string
  reference_number?: string
  payment_gateway?: string
  gateway_transaction_id?: string
  is_in_kind: boolean
  in_kind_description?: string
  fair_market_value?: number
  is_tax_deductible: boolean
  tax_receipt_number?: string
  tax_receipt_issued_date?: string
  pledge_id?: string
  journal_entry_id?: string
  is_posted_to_ledger: boolean
  status: string
  notes?: string
  created_at: string
  updated_at: string
}

export interface Expense {
  id: string
  church_id: string
  expense_number: string
  expense_date: string
  vendor_id?: string
  vendor_name?: string
  description: string
  expense_category?: string
  amount: number
  vat_amount: number
  total_amount: number
  currency: string
  invoice_number?: string
  invoice_date?: string
  due_date?: string
  expense_account_id?: string
  fund_id?: string
  department_id?: string
  project_id?: string
  payment_method?: string
  payment_reference?: string
  payment_date?: string
  amount_paid?: number
  balance_due?: number
  is_fully_paid?: boolean
  status: 'draft' | 'submitted' | 'pending_approval' | 'approved' | 'rejected' | 'paid' | 'partially_paid' | 'cancelled'
  submitted_by?: string
  submitted_at?: string
  approved_by?: string
  approved_at?: string
  rejected_by?: string
  rejected_at?: string
  requires_approval: boolean
  approval_level: number
  current_approver_id?: string
  journal_entry_id?: string
  is_posted_to_ledger: boolean
  attachment_urls?: string[]
  notes?: string
  rejection_reason?: string
  created_at: string
  updated_at: string
  created_by?: string
  updated_by?: string
}

export interface Asset {
  id: string
  church_id: string
  asset_code: string
  asset_name: string
  asset_category: string
  asset_subcategory?: string
  description?: string
  serial_number?: string
  barcode?: string
  location?: string
  responsible_person_id?: string
  purchase_date: string
  purchase_cost: number
  vendor_id?: string
  invoice_number?: string
  useful_life_years?: number
  useful_life_months?: number
  depreciation_method: 'straight_line' | 'declining_balance' | 'units_of_production' | 'none'
  salvage_value: number
  accumulated_depreciation: number
  book_value: number
  fair_market_value?: number
  last_revaluation_date?: string
  depreciation_start_date?: string
  last_depreciation_date?: string
  annual_depreciation_amount?: number
  asset_account_id?: string
  accumulated_depreciation_account_id?: string
  depreciation_expense_account_id?: string
  disposal_date?: string
  disposal_method?: string
  disposal_proceeds?: number
  disposal_gain_loss?: number
  status: 'active' | 'disposed' | 'written_off' | 'fully_depreciated' | 'under_construction'
  photo_urls?: string[]
  document_urls?: string[]
  notes?: string
  created_at: string
  updated_at: string
}

export interface Vendor {
  id: string
  church_id: string
  vendor_code: string
  vendor_name: string
  vendor_type?: string
  vendor_category?: string
  contact_person?: string
  email?: string
  phone?: string
  mobile?: string
  physical_address?: string
  postal_address?: string
  city?: string
  province?: string
  postal_code?: string
  country: string
  tax_number?: string
  is_vat_vendor: boolean
  bank_name?: string
  bank_account_number?: string
  bank_branch_code?: string
  account_type?: string
  payment_terms?: string
  payment_terms_days: number
  credit_limit?: number
  default_expense_account_id?: string
  payable_account_id?: string
  is_active: boolean
  is_approved: boolean
  rating?: number
  notes?: string
  created_at: string
  updated_at: string
}

export interface Budget {
  id: string
  church_id: string
  budget_code: string
  budget_name: string
  budget_type: 'annual' | 'quarterly' | 'monthly' | 'project' | 'campaign'
  fiscal_year: number
  start_date: string
  end_date: string
  total_revenue_budget: number
  total_expense_budget: number
  net_budget: number
  status: 'draft' | 'submitted' | 'approved' | 'active' | 'closed' | 'revised'
  approved_by?: string
  approved_at?: string
  is_revision: boolean
  original_budget_id?: string
  revision_number: number
  revision_reason?: string
  description?: string
  notes?: string
  lines?: BudgetLine[]
  created_at: string
  updated_at: string
}

export interface BudgetLine {
  id: string
  budget_id: string
  church_id: string
  account_id: string
  fund_id?: string
  department_id?: string
  project_id?: string
  annual_budget: number
  period_1_budget: number
  period_2_budget: number
  period_3_budget: number
  period_4_budget: number
  period_5_budget: number
  period_6_budget: number
  period_7_budget: number
  period_8_budget: number
  period_9_budget: number
  period_10_budget: number
  period_11_budget: number
  period_12_budget: number
  notes?: string
  account?: ChartOfAccount
}

export interface BankAccount {
  id: string
  church_id: string
  account_name: string
  account_number: string
  bank_name: string
  branch_name?: string
  branch_code?: string
  swift_code?: string
  account_type?: string
  currency: string
  gl_account_id?: string
  current_balance: number
  statement_balance: number
  last_reconciled_balance: number
  last_reconciled_date?: string
  is_active: boolean
  is_primary: boolean
  has_online_access: boolean
  online_banking_url?: string
  notes?: string
  created_at: string
  updated_at: string
}

export interface BankReconciliation {
  id: string
  church_id: string
  bank_account_id: string
  reconciliation_date: string
  statement_date: string
  fiscal_year: number
  fiscal_period: number
  opening_balance: number
  closing_balance: number
  statement_balance: number
  total_deposits_in_transit: number
  total_outstanding_checks: number
  total_adjustments: number
  calculated_gl_balance: number
  difference: number
  is_reconciled: boolean
  reconciled_by?: string
  reconciled_at?: string
  notes?: string
  items?: BankReconciliationItem[]
  created_at: string
  updated_at: string
}

export interface BankReconciliationItem {
  id: string
  reconciliation_id: string
  item_type: string
  transaction_date?: string
  description?: string
  reference_number?: string
  amount: number
  is_cleared: boolean
  cleared_date?: string
  journal_entry_line_id?: string
  notes?: string
}

export interface Fund {
  id: string
  church_id: string
  fund_code: string
  fund_name: string
  restriction_type: 'unrestricted' | 'temporarily_restricted' | 'permanently_restricted'
  description?: string
  purpose?: string
  donor_name?: string
  restriction_start_date?: string
  restriction_end_date?: string
  restriction_conditions?: string
  current_balance: number
  asset_account_id?: string
  equity_account_id?: string
  is_active: boolean
  is_endowment: boolean
  created_at: string
  updated_at: string
}

export interface FinancialReport {
  category: string
  subcategory?: string
  account_code?: string
  account_name?: string
  amount: number
}

export interface FinancialRatio {
  ratio_name: string
  ratio_value: number
  ratio_description: string
}

// Financial Report Types for IAS-compliant statements
export interface AccountLineItem {
  account_id: string
  account_code: string
  account_name: string
  balance: number
}

export interface StatementOfFinancialPosition {
  assets: AccountLineItem[]
  liabilities: AccountLineItem[]
  net_assets: AccountLineItem[]
  total_assets: number
  total_liabilities: number
  total_net_assets: number
}

export interface RevenueExpenseLineItem {
  account_id: string
  account_code: string
  account_name: string
  amount: number
}

export interface StatementOfActivities {
  revenue: RevenueExpenseLineItem[]
  expenses: RevenueExpenseLineItem[]
  total_revenue: number
  total_expenses: number
  net_income: number
}

export interface CashFlowStatement {
  operating_activities: {
    contributions: number
    expenses: number
    net: number
  }
  investing_activities: {
    asset_purchases: number
    asset_sales: number
    net: number
  }
  financing_activities: {
    borrowings: number
    repayments: number
    net: number
  }
  net_change: number
  beginning_balance: number
  ending_balance: number
}

export interface BudgetVarianceLineItem {
  account_id: string
  account_code: string
  account_name: string
  budgeted: number
  actual: number
  variance: number
  variance_percent: number
}

export interface BudgetVarianceReport {
  line_items: BudgetVarianceLineItem[]
  total_budgeted: number
  total_actual: number
  total_variance: number
}

// =====================================================
// CHART OF ACCOUNTS SERVICE
// =====================================================

export class ChartOfAccountsService {
  /**
   * Get all accounts for a church
   */
  static async getAccounts(churchId: string, filters?: {
    accountType?: string
    isActive?: boolean
    search?: string
  }): Promise<ChartOfAccount[]> {
    let query = supabase
      .from('chart_of_accounts')
      .select('*')
      .eq('church_id', churchId)
      .order('account_code')

    if (filters?.accountType) {
      query = query.eq('account_type', filters.accountType)
    }

    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive)
    }

    if (filters?.search) {
      query = query.or(`account_code.ilike.%${filters.search}%,account_name.ilike.%${filters.search}%`)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  }

  /**
   * Get account by ID
   */
  static async getAccountById(accountId: string): Promise<ChartOfAccount | null> {
    const { data, error } = await supabase
      .from('chart_of_accounts')
      .select('*')
      .eq('id', accountId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create new account
   */
  static async createAccount(account: Partial<ChartOfAccount>): Promise<ChartOfAccount> {
    const { data, error } = await supabase
      .from('chart_of_accounts')
      .insert([account])
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Update existing account
   */
  static async updateAccount(accountId: string, updates: Partial<ChartOfAccount>): Promise<ChartOfAccount> {
    const { data, error } = await supabase
      .from('chart_of_accounts')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', accountId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Delete account (soft delete by setting is_active = false)
   */
  static async deleteAccount(accountId: string): Promise<void> {
    const { error } = await supabase
      .from('chart_of_accounts')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', accountId)

    if (error) throw error
  }

  /**
   * Get account hierarchy (parent-child relationships)
   */
  static async getAccountHierarchy(churchId: string): Promise<ChartOfAccount[]> {
    const { data, error } = await supabase
      .from('chart_of_accounts')
      .select('*')
      .eq('church_id', churchId)
      .is('parent_account_id', null)
      .eq('is_active', true)
      .order('account_code')

    if (error) throw error
    return data || []
  }

  /**
   * Get sub-accounts for a parent account
   */
  static async getSubAccounts(parentAccountId: string): Promise<ChartOfAccount[]> {
    const { data, error } = await supabase
      .from('chart_of_accounts')
      .select('*')
      .eq('parent_account_id', parentAccountId)
      .eq('is_active', true)
      .order('account_code')

    if (error) throw error
    return data || []
  }

  /**
   * Initialize standard chart of accounts for a new church
   */
  static async initializeStandardChartOfAccounts(churchId: string, userId: string): Promise<void> {
    const standardAccounts = [
      // ASSETS
      { code: '1000', name: 'ASSETS', type: 'asset', subtype: 'current_asset', category: 'Current Assets' },
      { code: '1010', name: 'Cash - Operating Account', type: 'asset', subtype: 'cash_and_equivalents', category: 'Current Assets' },
      { code: '1020', name: 'Cash - Savings Account', type: 'asset', subtype: 'cash_and_equivalents', category: 'Current Assets' },
      { code: '1100', name: 'Accounts Receivable', type: 'asset', subtype: 'receivables', category: 'Current Assets' },
      { code: '1200', name: 'Prepaid Expenses', type: 'asset', subtype: 'prepayments', category: 'Current Assets' },
      { code: '1500', name: 'Property & Equipment', type: 'asset', subtype: 'fixed_asset', category: 'Non-Current Assets' },
      { code: '1510', name: 'Land & Buildings', type: 'asset', subtype: 'fixed_asset', category: 'Non-Current Assets' },
      { code: '1520', name: 'Furniture & Fixtures', type: 'asset', subtype: 'fixed_asset', category: 'Non-Current Assets' },
      { code: '1530', name: 'Equipment', type: 'asset', subtype: 'fixed_asset', category: 'Non-Current Assets' },
      { code: '1540', name: 'Vehicles', type: 'asset', subtype: 'fixed_asset', category: 'Non-Current Assets' },
      { code: '1550', name: 'Accumulated Depreciation', type: 'asset', subtype: 'fixed_asset', category: 'Non-Current Assets' },

      // LIABILITIES
      { code: '2000', name: 'LIABILITIES', type: 'liability', subtype: 'current_liability', category: 'Current Liabilities' },
      { code: '2010', name: 'Accounts Payable', type: 'liability', subtype: 'accounts_payable', category: 'Current Liabilities' },
      { code: '2020', name: 'Accrued Expenses', type: 'liability', subtype: 'accrued_expense', category: 'Current Liabilities' },
      { code: '2030', name: 'Deferred Revenue', type: 'liability', subtype: 'deferred_revenue', category: 'Current Liabilities' },
      { code: '2100', name: 'Loans Payable', type: 'liability', subtype: 'loans_payable', category: 'Non-Current Liabilities' },

      // EQUITY/NET ASSETS
      { code: '3000', name: 'NET ASSETS', type: 'equity', subtype: 'net_assets_unrestricted', category: 'Net Assets' },
      { code: '3010', name: 'Net Assets - Unrestricted', type: 'equity', subtype: 'net_assets_unrestricted', category: 'Net Assets' },
      { code: '3020', name: 'Net Assets - Temporarily Restricted', type: 'equity', subtype: 'net_assets_temporarily_restricted', category: 'Net Assets' },
      { code: '3030', name: 'Net Assets - Permanently Restricted', type: 'equity', subtype: 'net_assets_permanently_restricted', category: 'Net Assets' },

      // REVENUE
      { code: '4000', name: 'REVENUE', type: 'revenue', subtype: 'contribution_revenue', category: 'Contributions' },
      { code: '4010', name: 'Tithes', type: 'revenue', subtype: 'contribution_revenue', category: 'Contributions' },
      { code: '4020', name: 'Offerings', type: 'revenue', subtype: 'contribution_revenue', category: 'Contributions' },
      { code: '4030', name: 'Donations', type: 'revenue', subtype: 'contribution_revenue', category: 'Contributions' },
      { code: '4100', name: 'Grants', type: 'revenue', subtype: 'grant_revenue', category: 'Grants' },
      { code: '4200', name: 'Program Revenue', type: 'revenue', subtype: 'program_revenue', category: 'Program Services' },
      { code: '4300', name: 'Investment Income', type: 'revenue', subtype: 'investment_income', category: 'Other Revenue' },
      { code: '4900', name: 'Other Revenue', type: 'revenue', subtype: 'other_revenue', category: 'Other Revenue' },

      // EXPENSES
      { code: '5000', name: 'EXPENSES', type: 'expense', subtype: 'program_expense', category: 'Program Expenses' },
      { code: '5100', name: 'Ministry & Outreach', type: 'expense', subtype: 'program_expense', category: 'Program Expenses' },
      { code: '5200', name: 'Worship & Music', type: 'expense', subtype: 'program_expense', category: 'Program Expenses' },
      { code: '5300', name: 'Children & Youth Programs', type: 'expense', subtype: 'program_expense', category: 'Program Expenses' },
      { code: '6000', name: 'Personnel Expenses', type: 'expense', subtype: 'personnel_expense', category: 'Personnel' },
      { code: '6010', name: 'Salaries & Wages', type: 'expense', subtype: 'personnel_expense', category: 'Personnel' },
      { code: '6020', name: 'Employee Benefits', type: 'expense', subtype: 'personnel_expense', category: 'Personnel' },
      { code: '6030', name: 'Payroll Taxes', type: 'expense', subtype: 'personnel_expense', category: 'Personnel' },
      { code: '7000', name: 'Administrative Expenses', type: 'expense', subtype: 'administrative_expense', category: 'Administration' },
      { code: '7010', name: 'Office Supplies', type: 'expense', subtype: 'administrative_expense', category: 'Administration' },
      { code: '7020', name: 'Professional Fees', type: 'expense', subtype: 'administrative_expense', category: 'Administration' },
      { code: '7030', name: 'Insurance', type: 'expense', subtype: 'administrative_expense', category: 'Administration' },
      { code: '8000', name: 'Occupancy Expenses', type: 'expense', subtype: 'occupancy_expense', category: 'Facilities' },
      { code: '8010', name: 'Rent/Mortgage', type: 'expense', subtype: 'occupancy_expense', category: 'Facilities' },
      { code: '8020', name: 'Utilities', type: 'expense', subtype: 'occupancy_expense', category: 'Facilities' },
      { code: '8030', name: 'Maintenance & Repairs', type: 'expense', subtype: 'occupancy_expense', category: 'Facilities' },
      { code: '8100', name: 'Depreciation Expense', type: 'expense', subtype: 'depreciation_expense', category: 'Non-Cash Expenses' },
    ]

    const accountsToInsert = standardAccounts.map(acc => ({
      church_id: churchId,
      account_code: acc.code,
      account_name: acc.name,
      account_type: acc.type,
      account_subtype: acc.subtype,
      balance_sheet_category: acc.category,
      income_statement_category: acc.category,
      is_active: true,
      is_system_account: true,
      allow_manual_entry: !acc.code.endsWith('000'), // Header accounts don't allow manual entry
      opening_balance: 0,
      current_balance: 0,
      created_by: userId,
    }))

    const { error } = await supabase
      .from('chart_of_accounts')
      .insert(accountsToInsert)

    if (error) throw error
  }
}

// Continue in next file part...
